// @ts-strict-ignore
import type {
  RuleBuilderRule,
  RuleInsertProps,
  RuleInsertionPosition,
  GroupRule,
  ThresholdRule,
  GroupType,
  RuleTree,
} from 'flow/service/rules';
import {
  buildGroupRule,
  buildRuleState,
  buildThresholdRule,
  deleteRuleFromRuleTree,
  findRule,
  insertRuleIntoRuleTree,
  isGroupRule,
  toggleGroupTypeInRuleTree,
  groupRulesInRuleTree,
} from 'flow/service/rules';
import type { AppDispatch, State } from '~/store';
import type { Action, Threshold, SchemaInstance } from 'flow/store/engine/types';
import { OperationMessageType } from 'flow/store/engine/types';
import { v4 as uuidv4 } from 'uuid';
import {
  addThreshold,
  deleteThreshold,
  setRuleBuilderActiveSubEditor,
  updateAction,
  updateThresholdAgnostic,
  updateWorkflowDataDependencies,
  updateWorkflowSnapshots,
  dismissWorkflowOperationMessage,
  createWorkflowOperationMessage,
  appendThresholdToLinkedAction,
  removeThresholdFromLinkedAction,
  setRuleBuilderIsDragging,
  removeEphemeralReasonCodeGroupFromProps,
  resetRuleBuilderPendingGroup,
} from '../slice';
import { findLastOperation } from 'flow/service/assistance';
import { RuleUpdateOperationType } from 'flow/store/engine/update/constants';
import { THRESHOLD_UPDATE_TYPE } from '../constants';
import type { ThresholdFieldName } from '../constants';
import { snapshotAndCloneWorkflowData } from '../helpers';
import { containsReasonCodeGroup, isSelfReferencingThreshold } from 'flow/service/thresholds';
import {
  selectFeatureFlagsForStore,
  selectRuleBuilderPendingGroupInfo,
  selectThresholdById,
  selectThresholds,
  selectWorkflowData,
  selectWorkflowOperationDescs,
  selectWorkflowProps,
} from '../selectors';
import { captureSelfReferencingThresholdError } from 'flow/store/engine/validation/thresholds';
import {
  generateThreshold,
  updateThresholdLeftSide,
  updateThresholdSimpleValue,
  updateThresholdRightAction,
  updateThresholdRightService,
} from 'flow/store/engine/update/thresholds';
import { isActionOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';
import {
  getReasonCodeGroupFromThreshold,
  isReasonCodeGroupEphemeral,
  isReasonCodeGroupReferencedInAnyThreshold,
} from 'flow/service/reasonCodes';

// ================ Rule State Modification Thunks ================

/** Adds a group to an action's rules & updates Workflow Snapshots */
export function groupRulesInRuleStateThunk(payload: {
  action: Action;
  treeRoot: RuleTree;
  insertProps: {
    groupType: GroupType;
    endIndex: number;
  };
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { action, treeRoot, insertProps } = payload;
    const { workflowSnapshots } = snapshotAndCloneWorkflowData(state.workflowEditor);
    const pendingGroupInfo = selectRuleBuilderPendingGroupInfo(state);

    const groupRuleTreeProps = {
      parentRule: findRule(treeRoot, { ruleId: pendingGroupInfo.parentRuleId }),
      groupToInsert: buildGroupRule({ groupType: insertProps.groupType }),
      indices: {
        start: pendingGroupInfo.startIndex,
        end: insertProps.endIndex,
      },
    };

    const groupedRuleTree = groupRulesInRuleTree(treeRoot, groupRuleTreeProps);
    const groupedRuleState = buildRuleState(groupedRuleTree);

    dispatch(updateAction({
      path: `${action.publicId}.rules`,
      value: groupedRuleState,
    }));

    dispatch(updateWorkflowSnapshots({ workflowSnapshots }));
  };
}
export type InsertThresholdFromThresholdPayload = {
  action: Action;
  treeRoot: RuleTree;
  insertProps: {
    threshold: Threshold;
    relativelyPositionedRule: RuleBuilderRule;
    position: RuleInsertionPosition;
  };
};

export type InsertThresholdFromWorkflowStatePayload = {
  action: Action;
  treeRoot: RuleTree;
  insertProps: {
    publicId: Threshold['publicId'];
    relativelyPositionedRule: RuleBuilderRule;
    position: RuleInsertionPosition;
  };
};
export type InsertThresholdIntoStateThunkPayload = (
  InsertThresholdFromThresholdPayload | InsertThresholdFromWorkflowStatePayload
);

/** Adds a threshold to threshold state & to an action's rule tree */
export function insertThresholdIntoStateThunk(
  payload: InsertThresholdIntoStateThunkPayload,
): (dispatch: AppDispatch, getState: () => State) => void {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { action, treeRoot, insertProps } = payload;
    const { relativelyPositionedRule, position } = insertProps;

    const thresholdToInsert = 'threshold' in insertProps
      ? insertProps.threshold
      : generateThreshold({
        fromWorkflowState: {
          sourceAction: action,
          workflowData: selectWorkflowData(state),
          workflowProps: selectWorkflowProps(state),
          featureFlagsForStore: selectFeatureFlagsForStore(state),
          publicId: insertProps.publicId,
        },
      });

    if (isSelfReferencingThreshold(thresholdToInsert)) {
      captureSelfReferencingThresholdError({ threshold: thresholdToInsert, userAction: 'create' });
      return;
    }

    const { workflowSnapshots } = snapshotAndCloneWorkflowData(state.workflowEditor);

    dispatch(addThreshold({ threshold: thresholdToInsert }));

    const thresholdRule = buildThresholdRule({
      thresholdId: thresholdToInsert.publicId, rulePath: [],
    });

    const insertArgs: RuleInsertProps = {
      relativelyPositionedRule,
      ruleToInsert: thresholdRule,
      position,
    };

    const insertedRuleTree = insertRuleIntoRuleTree(treeRoot, insertArgs);
    const insertedRuleState = buildRuleState(insertedRuleTree);

    dispatch(updateAction({
      path: `${action.publicId}.rules`,
      value: insertedRuleState,
    }));

    if (isActionOfType(action, ActionType.PREVIEW)) {
      dispatch(
        appendThresholdToLinkedAction({
          thresholdId: thresholdToInsert.publicId,
        }),
      );
    }

    if (!thresholdToInsert.isPreview) {
      dispatch(updateWorkflowSnapshots({ workflowSnapshots }));
    }
  };
}

/** Deletes a group from an action's rule tree */
function deleteGroupFromStateThunk(payload: {
  action: Action;
  treeRoot: RuleTree;
  rule: GroupRule;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { workflowSnapshots } = snapshotAndCloneWorkflowData(state.workflowEditor);
    const { action, treeRoot, rule } = payload;

    const deletedRuleTree = deleteRuleFromRuleTree({ treeRoot, rule });
    const deletedRuleState = buildRuleState(deletedRuleTree);

    dispatch(updateAction({
      path: `${action.publicId}.rules`,
      value: deletedRuleState,
    }));

    dispatch(updateWorkflowSnapshots({ workflowSnapshots }));
  };
}

/**
* Deletes a threshold from an action's rules & from threshold state.
* Assumes that a threshold is never referenced by multiple actions.
*/
function deleteThresholdFromStateThunk(payload: {
  action: Action;
  treeRoot: RuleTree;
  rule: ThresholdRule;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { action, treeRoot, rule } = payload;
    const threshold = selectThresholdById(rule.id)(state);

    if (!threshold) return;

    const { workflowSnapshots } = snapshotAndCloneWorkflowData(state.workflowEditor);

    const deletedRuleTree = deleteRuleFromRuleTree({ treeRoot, rule });
    const deletedRuleState = buildRuleState(deletedRuleTree);

    dispatch(updateAction({
      path: `${action.publicId}.rules`,
      value: deletedRuleState,
    }));

    if (isSelfReferencingThreshold(threshold)) {
      captureSelfReferencingThresholdError({ threshold, userAction: 'remove' });
    }

    dispatch(deleteThreshold({ id: threshold.publicId }));

    if (isActionOfType(action, ActionType.PREVIEW)) {
      dispatch(
        removeThresholdFromLinkedAction({
          thresholdId: threshold.publicId,
        }),
      );
    }

    if (!threshold.isPreview) {
      dispatch(updateWorkflowSnapshots({ workflowSnapshots }));
    }

    /**
     * Thresholds can contain a reference to ReasonCodeGroups (RCG). if the RCG inside of
     * the threshold is ephemeral, and is not referenced in any other threshold,
     * it should be removed.
    */
    if (containsReasonCodeGroup(threshold)) {
      const thresholdsAfterRemoval = selectThresholds(getState());
      const reasonCodeGroup = getReasonCodeGroupFromThreshold(threshold);
      const isEphemeral = isReasonCodeGroupEphemeral(reasonCodeGroup);
      const isReferenced = isReasonCodeGroupReferencedInAnyThreshold({
        reasonCodeGroup,
        thresholds: thresholdsAfterRemoval,
      });

      if (isEphemeral && !isReferenced) {
        dispatch(removeEphemeralReasonCodeGroupFromProps({ reasonCodeGroup }));
      }
    }
  };
}

export function deleteRuleFromStateThunk(payload: {
  action: Action;
  treeRoot: RuleTree;
  rule: RuleBuilderRule;
}) {
  const { action, treeRoot, rule } = payload;

  if (isGroupRule(rule)) {
    return deleteGroupFromStateThunk({ action, treeRoot, rule });
  }

  return deleteThresholdFromStateThunk({ action, treeRoot, rule });
}

/**
* For drag and drop
* Assumes this will never be called on a preview threshold.
*/
export function moveThresholdInStateThunk(payload: {
  action: Action;
  treeRoot: RuleTree;
  rule: ThresholdRule;
  insertProps: {
    relativelyPositionedRule: RuleBuilderRule;
    position: RuleInsertionPosition;
  };
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const {
      action,
      treeRoot,
      rule,
      insertProps: { relativelyPositionedRule, position },
    } = payload;

    const { workflowSnapshots } = snapshotAndCloneWorkflowData(state.workflowEditor);

    const thresholdToInsert = structuredClone(rule);
    const deletedRuleTree = deleteRuleFromRuleTree({ treeRoot, rule });

    const insertProps: RuleInsertProps = {
      relativelyPositionedRule: findRule(deletedRuleTree, { ruleId: relativelyPositionedRule.id }),
      ruleToInsert: thresholdToInsert,
      position,
    };

    const movedRuleTree = insertRuleIntoRuleTree(deletedRuleTree, insertProps);
    const movedRuleState = buildRuleState(movedRuleTree);

    dispatch(updateAction({
      path: `${action.publicId}.rules`,
      value: movedRuleState,
    }));

    dispatch(updateWorkflowSnapshots({ workflowSnapshots }));
  };
}

/** Toggles grouptype of a group: or <-> and */
export function toggleGroupTypeInStateThunk(payload: {
  action: Action;
  treeRoot: RuleTree;
  rule: GroupRule;
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { action, treeRoot, rule } = payload;

    const { workflowSnapshots } = snapshotAndCloneWorkflowData(state.workflowEditor);

    const toggledRuleTree = toggleGroupTypeInRuleTree({ treeRoot, rule });
    const toggledRuleState = buildRuleState(toggledRuleTree);

    dispatch(updateAction({
      path: `${action.publicId}.rules`,
      value: toggledRuleState,
    }));

    // Show assistance message for boolean operator change
    dispatch(createWorkflowOperationMessage({
      object: {
        id: uuidv4(),
        publicId: action.publicId,
        type: OperationMessageType.actionRuleThreshold,
        timestamp: Date.now(),
        message: RuleUpdateOperationType.UPDATED_ALL_BOOL_OPERATORS_SAME_LEVEL,
        data: undefined,
      },
    }));

    dispatch(updateWorkflowSnapshots({ workflowSnapshots }));
  };
}

// ================ Threshold Update Thunk ================

export function updateThresholdThunk<V = never>(payload: {
  thresholdId: string;
  update: {
    type: typeof THRESHOLD_UPDATE_TYPE.VALUE;
    field: ThresholdFieldName;
    value: string | number | boolean | V;
  };
}): (dispatch: AppDispatch, getState: () => State) => void;

export function updateThresholdThunk(payload: {
  thresholdId: string;
  update: {
    type: typeof THRESHOLD_UPDATE_TYPE.LEFT_ACTION;
    actionId: string;
  };
}): (dispatch: AppDispatch, getState: () => State) => void;

export function updateThresholdThunk(payload: {
  thresholdId: string;
  update: {
    type: typeof THRESHOLD_UPDATE_TYPE.LEFT_ATTRIBUTE;
    attributeId: number;
  };
}): (dispatch: AppDispatch, getState: () => State) => void;

export function updateThresholdThunk(payload: {
  thresholdId: string;
  update: {
    type: typeof THRESHOLD_UPDATE_TYPE.RIGHT_ACTION;
    actionId: string;
  };
}): (dispatch: AppDispatch, getState: () => State) => void;

export function updateThresholdThunk(payload: {
  thresholdId: string;
  update: {
    type: typeof THRESHOLD_UPDATE_TYPE.RIGHT_ATTRIBUTE_ID;
    attributeId: number;
  };
}): (dispatch: AppDispatch, getState: () => State) => void;

export function updateThresholdThunk(payload: {
  thresholdId: string;
  update:
  | { type: typeof THRESHOLD_UPDATE_TYPE.VALUE; field: string; value: string | number | boolean }
  | { type: typeof THRESHOLD_UPDATE_TYPE.LEFT_ACTION; actionId: string }
  | { type: typeof THRESHOLD_UPDATE_TYPE.LEFT_ATTRIBUTE; attributeId: number }
  | { type: typeof THRESHOLD_UPDATE_TYPE.RIGHT_ACTION; actionId: string }
  | { type: typeof THRESHOLD_UPDATE_TYPE.RIGHT_ATTRIBUTE_ID; attributeId: number };
}) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const { thresholdId, update } = payload;

    const { workflowData, workflowSnapshots } = snapshotAndCloneWorkflowData(state.workflowEditor);
    const originalThreshold = workflowData.thresholds[thresholdId];
    if (!originalThreshold) return;

    if (isSelfReferencingThreshold(originalThreshold)) {
      captureSelfReferencingThresholdError({ threshold: originalThreshold, userAction: 'update' });
    }

    let updatedThreshold: Threshold;

    switch (update.type) {
      case THRESHOLD_UPDATE_TYPE.VALUE:
        updatedThreshold = updateThresholdSimpleValue({
          threshold: originalThreshold,
          field: update.field,
          value: update.value,
        });
        break;

      case THRESHOLD_UPDATE_TYPE.LEFT_ACTION:
        updatedThreshold = updateThresholdLeftSide({
          originalThreshold,
          leftActionId: update.actionId,
          workflowData: workflowData as SchemaInstance,
          workflowProps: selectWorkflowProps(state),
          featureFlags: selectFeatureFlagsForStore(state),
        });
        break;

      case THRESHOLD_UPDATE_TYPE.LEFT_ATTRIBUTE:
        updatedThreshold = updateThresholdLeftSide({
          originalThreshold,
          leftActionId: originalThreshold.left.actionId,
          leftAttributeId: update.attributeId,
          workflowData: workflowData as SchemaInstance,
          workflowProps: selectWorkflowProps(state),
          featureFlags: selectFeatureFlagsForStore(state),
        });
        break;

      case THRESHOLD_UPDATE_TYPE.RIGHT_ACTION:
        updatedThreshold = updateThresholdRightAction({
          originalThreshold,
          rightActionId: update.actionId,
          workflowData: workflowData as SchemaInstance,
          workflowProps: state.workflowEditor.workflowProps,
        });
        break;

      case THRESHOLD_UPDATE_TYPE.RIGHT_ATTRIBUTE_ID:
        updatedThreshold = updateThresholdRightService({
          originalThreshold,
          rightAction: workflowData.actions[originalThreshold.right.actionId],
          rightAttributeId: update.attributeId,
          workflowData: workflowData as SchemaInstance,
          workflowProps: state.workflowEditor.workflowProps,
        });
        break;

      default:
        throw new Error(`Unknown threshold update type: ${(update as any).type}`);
    }

    if (isSelfReferencingThreshold(updatedThreshold)) {
      captureSelfReferencingThresholdError({ threshold: updatedThreshold, userAction: 'update' });
    }

    dispatch(updateThresholdAgnostic({ threshold: updatedThreshold }));
    dispatch(updateWorkflowDataDependencies());
    dispatch(updateWorkflowSnapshots({ workflowSnapshots }));
  };
}

// ================ Rule UI State Thunks ================

export function resetRuleBuilderPendingGroupStateThunk(sourceActionId?: string) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const operationDescriptions = selectWorkflowOperationDescs(state);

    // Dismiss any existing CANCEL_PENDING_RULE_GROUP message (scoped to sourceActionId when provided)
    const existingMessage = findLastOperation({
      operations: operationDescriptions,
      publicId: sourceActionId,
      messages: [RuleUpdateOperationType.CANCEL_PENDING_RULE_GROUP],
    });

    if (existingMessage) {
      dispatch(dismissWorkflowOperationMessage({ id: existingMessage.id }));
    }

    dispatch(resetRuleBuilderPendingGroup());
  };
}

export function resetSubEditor() {
  return (dispatch: AppDispatch) => {
    dispatch(
      setRuleBuilderActiveSubEditor({
        thresholdId: null,
        type: null,
        side: null,
      }),
    );
  };
}

export function resetRuleBuilderUiState(sourceActionId: string) {
  return (dispatch: AppDispatch) => {
    dispatch(resetRuleBuilderPendingGroupStateThunk(sourceActionId));
    dispatch(setRuleBuilderIsDragging(false));
    dispatch(resetSubEditor());
  };
}
